import type { FilterDefinition, FilterValues } from "@/services/filter";

/** Result type with filter definition IDs as keys and value IDs as array values */
export type MappedFilterValues = Record<string, string[]>;

/**
 * Maps filter values from slugs (used in URL) to IDs (used in API).
 * - Keys are transformed from slugs to filter definition IDs
 * - Values are transformed from slugs to value IDs
 * - All values are returned as arrays (for consistent API format)
 * - Empty values are filtered out
 */
export function mapFilterSlugsToIds(filterValues: FilterValues, filterDefinitions: FilterDefinition[]): MappedFilterValues {
  const result: MappedFilterValues = {};

  for (const [key, value] of Object.entries(filterValues)) {
    // Skip empty values
    if (value === undefined || value === "") {
      continue;
    }
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }

    // Find the filter definition for this key (slug)
    const definition = filterDefinitions.find((d) => d.slug === key);

    if (!definition) {
      // No definition found, skip (can't map to ID)
      continue;
    }

    const definitionId = String(definition.id);

    // Map value slug(s) to ID(s), always as array
    if (Array.isArray(value)) {
      result[definitionId] = value.map((slug) => {
        const option = definition.values.find((v) => v.slug === slug);
        return option ? String(option.id) : slug;
      });
    } else {
      const option = definition.values.find((v) => v.slug === value);
      result[definitionId] = [option ? String(option.id) : value];
    }
  }

  return result;
}
